import React, { type FC, type PropsWithChildren } from 'react';

import { useSiteMetadata, setMetatagCookies } from '@lucid/utils';

import type { Template } from '../../types/types';

export interface SEOProps {
  entityMetatags?: Template.Metatag[] | null;
  entitySchemaMetatags?: string | null;
  location?: Partial<Location>;
}

const SEO: FC<PropsWithChildren<SEOProps>> = ({
  children,
  entityMetatags = [],
  entitySchemaMetatags = '',
  location,
}) => {
  const { siteUrl, title, favicon } = useSiteMetadata();
  setMetatagCookies(entityMetatags);
  let shouldOverrideCanonical = false;
  const tagsWithName = ['description', 'keywords', 'robots'];
  const hasTitle = !!entityMetatags?.find(({ key }) => key === 'title');

  return (
    <>
      {!hasTitle && (
        <title key="base-title" id="base-title">
          {title}
        </title>
      )}
      <link key="shortcut-icon" href={favicon} id="shortcut-icon" rel="shortcut icon" type="image/png" />
      {entityMetatags?.map(({ key, value, id }) => {
        let tag;
        if (key === 'title') {
          tag = (
            <title key={key} id={id || key}>
              {value}
            </title>
          );
        } else if (key === 'canonical') {
          tag = <link key={key} href={value!} id={id || key} rel={key} />;
          shouldOverrideCanonical = true;
        } else if (tagsWithName.includes(key!)) {
          tag = <meta key={key} content={value!} id={id || key!} name={key!} />;
        } else {
          tag = <meta key={key} content={value!} id={id || key!} property={key!} />;
        }

        return tag;
      })}
      {!shouldOverrideCanonical && (
        <link
          key={`${siteUrl}${location?.pathname}`}
          href={`${siteUrl}${location?.pathname}`}
          id="canonical"
          rel="canonical"
        />
      )}
      {entitySchemaMetatags && (
        <script key="schema-structured-data" id="schema-structured-data" type="application/ld+json">
          {entitySchemaMetatags}
        </script>
      )}
      {children}
    </>
  );
};

export { SEO };
export default SEO;
